import { apiService } from './api';
import { getFunctionUrl } from '../config/v2FunctionUrls';

/**
 * Valid warmup targets that map to backend services
 * - 'collections' → warms: getSystemCollections, getCollections, getCollection
 * - 'problems' → warms: getProblems, getStats, getCollectionProblems
 * - 'chat' → warms: sendChatMessage, getChatHistory
 */
export type WarmupTarget = 'collections' | 'problems' | 'chat';

/**
 * Lightweight warmup service for frontend-triggered backend warming.
 *
 * Fires fire-and-forget requests to warm backend Cloud Functions when users
 * land on key pages. This reduces cold start latency for subsequent requests.
 */
export const warmupService = {
  /**
   * Fire a warmup request to the backend.
   * Uses fire-and-forget pattern with keepalive to ensure request completes
   * even if user navigates away.
   *
   * @param targets - Array of service targets to warm
   */
  warm: (targets: WarmupTarget[]) => {
    const warmupUrl = getFunctionUrl('warmup');

    // Fire and forget - use keepalive to ensure request completes
    fetch(warmupUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // Required by backend for CSRF protection
      },
      body: JSON.stringify({ targets }),
      keepalive: true,
    }).catch(() => {
      // Silently ignore errors - warmup is non-critical
    });
  },

  /**
   * Warm services needed for HomePage
   * (Popular Collections, My Collections, Problems tab)
   */
  warmHomePage: () => {
    warmupService.warm(['collections', 'problems']);
  },

  /**
   * Warm services needed for CollectionPage
   * (Collection details + chat for AI assistant)
   */
  warmCollectionPage: () => {
    warmupService.warm(['collections', 'problems', 'chat']);
  },
};

/**
 * Configuration for which services to warm up (legacy)
 */
interface WarmupConfig {
  cache: boolean;
  collections: boolean;
  problems: boolean;
  search: boolean;
}

/**
 * Default warming configuration (legacy)
 */
const DEFAULT_WARMUP_CONFIG: WarmupConfig = {
  cache: true,
  collections: true,
  problems: true,
  search: true
};

/**
 * Warms up backend services to eliminate cold start delays (legacy)
 * This function runs independently and doesn't block the caller
 *
 * @deprecated Use warmupService.warm() instead for lighter-weight warmup
 * @param config - Optional configuration for selective warming
 * @returns Promise that resolves when warming is complete (can be ignored)
 */
export async function warmUpBackendServices(
  config: Partial<WarmupConfig> = {}
): Promise<void> {
  const warmupConfig = { ...DEFAULT_WARMUP_CONFIG, ...config };
  const warmupTasks: Promise<any>[] = [];

  console.log('🔥 Starting backend warm-up...');
  const startTime = performance.now();

  // Warm up cache service (Redis + Firestore initialization)
  if (warmupConfig.cache) {
    const warmCacheUrl = getFunctionUrl('warmCache');
    warmupTasks.push(
      fetch(warmCacheUrl, {
        method: 'POST',
        headers: {
          'X-Warmup-Request': 'true',
          'Content-Type': 'application/json'
        }
      }).catch(() => {
        console.log('⚠️ Cache warming failed (non-critical)');
      })
    );
  }

  // Warm up collections endpoint
  if (warmupConfig.collections) {
    warmupTasks.push(
      apiService.request('/collections?warm=true', {
        method: 'GET',
        timeout: 2000,
        headers: { 'X-Warmup-Request': 'true' }
      }).catch(() => {
        console.log('⚠️ Collections warming failed (non-critical)');
      })
    );
  }

  // Warm up batch problems fetching
  if (warmupConfig.problems) {
    warmupTasks.push(
      apiService.request('/problems/batch', {
        method: 'POST',
        body: JSON.stringify({ problemIds: ['__warm__'] }),
        timeout: 2000,
        headers: {
          'X-Warmup-Request': 'true',
          'Content-Type': 'application/json'
        }
      }).catch(() => {
        console.log('⚠️ Problems warming failed (non-critical)');
      })
    );
  }

  // Warm up search/AI generation function
  if (warmupConfig.search) {
    warmupTasks.push(
      apiService.request('/problems/search-or-generate', {
        method: 'POST',
        body: JSON.stringify({ query: '__warm__' }),
        timeout: 2000,
        headers: {
          'X-Warmup-Request': 'true',
          'Content-Type': 'application/json'
        }
      }).catch(() => {
        console.log('⚠️ Search warming failed (non-critical)');
      })
    );
  }

  // Execute all warming tasks in parallel
  try {
    await Promise.allSettled(warmupTasks);
    const duration = Math.round(performance.now() - startTime);
    console.log(`✅ Backend warm-up completed in ${duration}ms`);
  } catch (error) {
    // This should never happen due to individual catches, but just in case
    console.log('⚠️ Backend warm-up had errors (non-critical):', error);
  }
}

/**
 * Utility to check if services are warmed up
 * Useful for showing loading states or debugging
 */
export async function checkWarmupStatus(): Promise<{
  cache: boolean;
  isWarm: boolean;
  lastWarmup: Date | null;
}> {
  try {
    const cacheStatusUrl = getFunctionUrl('cacheStatus');
    const response = await fetch(cacheStatusUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const jsonResponse = await response.json();
    // The response has success and data properties
    const data = jsonResponse.data || jsonResponse;
    
    return {
      cache: data.isAvailable || false,
      isWarm: data.isWarmed || false,
      lastWarmup: data.lastWarmup ? new Date(data.lastWarmup) : null
    };
  } catch {
    return {
      cache: false,
      isWarm: false,
      lastWarmup: null
    };
  }
}